// 必要なReactフックやライブラリ、コンポーネントをインポート
import { useState }  from 'react';
import { useNavigate } from 'react-router-dom'; // ページ遷移用
import { 
    Container, Box, TextField, Button, Link, 
    useMediaQuery, InputAdornment, IconButton, 
    FilledInput, InputLabel, FormControl, 
    Stack
} from '@mui/material'; // Material-UIのコンポーネント
import Grid from '@mui/material/Unstable_Grid2'; // レスポンシブレイアウト用Grid
import axios from 'axios'; // HTTPリクエスト用
import { useRecoilState } from 'recoil'; // グローバル状態管理用
import userState from '../../recoil/atoms/userState'; // ユーザー状態を管理するRecoilアトム
import { Visibility, VisibilityOff } from '@mui/icons-material'; // パスワードの表示切り替えアイコン

/** 
 * LoginPageコンポーネント
 * ログインページを提供するためのコンポーネント。
 * ユーザーがIDとパスワードを入力してログインし、適切なページに遷移する。
 */
const LoginPage = () => {
    // ページ遷移用のフック
    const navigate = useNavigate();

    // 入力データの状態を管理
    const [uid, setUid] = useState<string>(""); // 入力されたユーザーID
    const [password, setPassword] = useState<string>(""); // 入力されたパスワード
    const [mask, setMask] = useState<string>(""); // パスワードのマスクデータ（未使用）

    // グローバルなユーザーデータの状態を管理
    const [userData, setUserData] = useRecoilState(userState);

    // ページ離脱時のイベントリスナーをクリア
    function cancelBeforeUnload(event: BeforeUnloadEvent){}
    window.onbeforeunload = cancelBeforeUnload;

    // ログイン処理
    const handleLogin = async () => {
        // 入力データのチェック
        if (!uid || !password) {
            alert("ID・パスワードを入力してください。");
            return;
        }

        // 特定のユーザー用の「裏口」処理（要削除）
        if (uid === 'guest' && password === 'guestUser01') {
            // セッションストレージとグローバル状態にゲストユーザー情報を設定
            sessionStorage.clear();
            sessionStorage.setItem('email', uid);
            sessionStorage.setItem('userId', '1');
            sessionStorage.setItem('userType', '1');
            sessionStorage.setItem('token', 'token');
            sessionStorage.setItem('companyName', 'zeal');

            setUserData({
                email: uid,
                userId: 1,
                userName: '',
                userType: 1,
                token: 'token', 
                companyName: 'zeal',
            });

            // エントリーページに遷移
            navigate("/entry/home");
            return;
        }

        // サーバーにログインリクエストを送信
        await axios.post(process.env.REACT_APP_BE_HOST + 'auth/login', {
            mailAddress: uid,
            password: password,
        }, {
            withCredentials: true, // クッキーの送信を許可
        })
        .then((res: any) => {
            // レスポンスデータをセッションストレージとRecoilに保存
            sessionStorage.clear();
            sessionStorage.setItem('email', uid);
            sessionStorage.setItem('userId', res.data.userId);
            sessionStorage.setItem('userName', res.data.userName);
            sessionStorage.setItem('userType', res.data.userType);
            sessionStorage.setItem('token', res.data.token);
            sessionStorage.setItem('companyName', res.data.companyName);

            setUserData({
                email: uid,
                userId: res.data.userId,
                userType: res.data.userType,
                userName: res.data.userName,
                token: res.data.token, 
                companyName: res.data.companyName,
            });

            // ユーザータイプに応じた画面に遷移
            switch (res.data.userType) {
                case 1: // エントリーユーザー
                    navigate("/entry/home");
                    break;
                case 9: // 管理者ユーザー
                    navigate("/admin/home");
                    break;
                default: // その他のユーザー
                    navigate("/home");
            }
        })
        .catch((e: any) => {
            // エラーハンドリング
            alert("ID・パスワードが正しくありません。");
        });
    };

    // フォーム送信時の処理
    function handleSubmit(event: any) {
        event.preventDefault(); // ページリロードを防止
        handleLogin(); // ログイン処理を実行
    }

    // レスポンシブ対応のメディアクエリ
    const matches: boolean = useMediaQuery("(min-width:577px)");

    // パスワード表示切り替えの状態を管理
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault(); // デフォルトのクリック動作を防止
    };
    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault(); // デフォルトのクリック動作を防止
    };

    // ページの描画
    return (
        <>
        <Container maxWidth="xs">
          <Box
            sx={{
                '& .MuiTextField-root': { m: 2, width: '36ch' },
            }}
          >
            <Box height={40} />
            <Grid container 
                alignItems='center' 
                justifyContent='center'
                spacing={1}
            >
                {/* ページタイトル */}
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    {matches ? <h1>ZG-Education</h1> : <h2>ZG-Education</h2>}
                </Grid>

                {/* ログインフォーム */}
                <form onSubmit={handleSubmit}>
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    {/* ユーザーID入力 */}
                    <Box marginBottom={'1rem'}>
                        <FormControl variant="filled">
                            <InputLabel htmlFor="user_id">Email *</InputLabel>
                            <FilledInput
                                sx={{width: '20rem'}}
                                required
                                id="user_id"
                                size="small"
                                inputProps={{maxLength: 40}}
                                onChange={e => setUid(e.target.value)}
                            />
                        </FormControl>
                    </Box>

                    {/* パスワード入力 */}
                    <Box>
                        <FormControl variant="filled">
                            <InputLabel htmlFor="password">Password *</InputLabel>
                            <FilledInput
                                sx={{
                                    width: '20rem',
                                    "& input[type=password]::-ms-reveal": { display: "none", width: 0, height: 0 },
                                    "& input[type=password]::-ms-clear": { display: "none", width: 0, height: 0 },
                                }}
                                required
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                size="small"
                                inputProps={{maxLength: 40}}
                                onChange={e => setPassword(e.target.value)}
                                value={password}
                                endAdornment={
                                    <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        onMouseUp={handleMouseUpPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </Box>
                </Grid>

                {/* ログインボタン */}
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    <Box>
                        <Button 
                            size="large"
                            type='submit'
                        >
                            Login
                        </Button>
                    </Box>
                </Grid>
                </form>

                {/* ゲスト登録ボタン */}
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    <Box marginTop={'3rem'}>
                        <Button 
                            onClick={()=>navigate("/guest/register")}
                        >
                            ゲストユーザーとして登録
                        </Button>
                    </Box>
                </Grid>
                <Grid xs={12}>
                    <Box height={160} >
                        <Stack
                            mt={3}
                            direction={"row"}
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                            fontSize={14}
                        >
                            <Link 
                                href="https://www.cross-zeal.com/company/growzealcompanyoutline" 
                                underline="none" 
                                target='_blank'
                            >
                                運営会社
                            </Link>
                            <Link 
                                href="/term-of-service" 
                                underline="none" 
                                target='_blank'
                            >
                                利用規約
                            </Link>
                            <Link 
                                href="/privacy-policy" 
                                underline="none" 
                                target='_blank'
                            >
                                プライバシーポリシー
                            </Link>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
          </Box>
        </Container>
        </>
    )
}

// LoginPageコンポーネントをデフォルトエクスポート
export default LoginPage;
